<script setup lang="ts">
import CDashboardClassroomResultsDiagnostic from '@/presentation/components/dashboard/CDashboardClassroomResultsDiagnostic.vue'
import CDashboardClassroomResultsCourse from '@/presentation/components/dashboard/CDashboardClassroomResultsCourse.vue'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useDashboardClassroomStore } from '@/presentation/stores/dashboard/dashboardClassroom.ts'
import { useDashboardClassroomResultStore } from '@/presentation/stores/dashboard/dashboardClassroomResult.ts'
import { useToast } from 'primevue/usetoast'
import type { DashboardClassroomModuleResult } from '@/core/domain/dashboard/DashboardClassroomModuleResult'
import type { ClassroomDiagnosticStudentResult } from '@/core/domain/dashboard/ClassroomDiagnosticStudentResult'

// Props inherited from router: schoolId, classroomId
const props = defineProps<{
  schoolId: number
  classroomId: number
}>()

// Store
const dashboardClassroomStore = useDashboardClassroomStore()
const dashboardClassroomResultStore = useDashboardClassroomResultStore()

// Store Refs
const {
  dashboardClassroomStoreIsLoading,
  dashboardSelectedClassroom,
  dashboardClassroomAssignments
} = storeToRefs(useDashboardClassroomStore())

// Local state management
const loading = ref(true)
const error = ref<string | null>(null)
const moduleResults = ref<DashboardClassroomModuleResult[]>([])
const diagnosticResults = ref<ClassroomDiagnosticStudentResult[]>([])

// Get results for a specific module
const getModuleResults = (moduleId: number): DashboardClassroomModuleResult | undefined => {
  return moduleResults.value.find(
    (module: DashboardClassroomModuleResult) => module.learningModuleId === moduleId
  )
}

// Composables and external dependencies
const toast = useToast()

// Store methods
const { setDashboardSelectedClassroomById, fetchDashboardClassroomAssignments } =
  useDashboardClassroomStore()
const { fetchClassroomDiagnosticResults, fetchClassroomModuleResults } =
  dashboardClassroomResultStore

// Load classroom results data (diagnostics and modules)
const loadClassroomResultsData = async () => {
  try {
    loading.value = true
    error.value = null

    // Ensure school and classroom are selected
    if (!dashboardSelectedClassroom.value) {
      await setDashboardSelectedClassroomById(props.schoolId, props.classroomId)
    }

    // Load assignments (modularization) to get modules
    if (!dashboardClassroomAssignments.value) {
      await fetchDashboardClassroomAssignments()
    }

    // Load diagnostic and module results in parallel
    const [diagnosticData, moduleData] = await Promise.all([
      fetchClassroomDiagnosticResults(true), // Force refresh for fresh data
      fetchClassroomModuleResults(true),
      dashboardClassroomStore.fetchDashboardClassroomDiagnosticState()
    ])

    // Store results in local state
    if (diagnosticData) {
      diagnosticResults.value = diagnosticData
    }
    if (moduleData) {
      moduleResults.value = moduleData
    }
  } catch (err) {
    console.error('Error loading classroom results:', err)
    error.value = err instanceof Error ? err.message : 'An error occurred while loading data'

    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.value,
      life: 5000
    })
  } finally {
    await nextTick(() => {
      // Ensure store is updated before changing loading state
      loading.value = false
    })
  }
}

// Computed properties
const isLoading = computed(() => {
  return loading.value || dashboardClassroomStoreIsLoading.value
})

onMounted(async () => {
  await loadClassroomResultsData()
})

onUnmounted(() => {
  // Reset results store when leaving the page
  dashboardClassroomResultStore.reset()
})
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Error state -->
    <div v-if="error" class="p-message p-message-error mb-4 w-full">
      <div class="p-message-text">{{ error }}</div>
    </div>

    <!-- Loading state -->
    <div v-else-if="isLoading" class="flex flex-col gap-4">
      <div class="p-skeleton mb-4 h-60"></div>
      <div class="p-skeleton h-80"></div>
    </div>

    <!-- Main content -->
    <template v-else>
      <!-- Global classroom diagnostics -->
      <c-dashboard-classroom-results-diagnostic
        :schoolId="props.schoolId"
        :classroomId="props.classroomId"
        :diagnosticResults="diagnosticResults"
      />

      <!-- Module progression blocks -->
      <template v-if="dashboardClassroomAssignments">
        <template
          v-for="(module, i) in moduleResults"
          :key="module.learningModuleId"
        >
          <!-- Module results block (if visible and has results) -->
          <c-dashboard-classroom-results-course
            v-if="module.visible"
            :schoolId="props.schoolId"
            :classroomId="props.classroomId"
            :moduleId="module.learningModuleId"
            :courseId="module.learningCourseId"
            :title="`Module ${module.displayOrder} : ${module.learningModuleTitle}`"
            :moduleResults="getModuleResults(module.learningModuleId)!"
          />

          <!-- Module not yet visible placeholder -->
          <div v-else class="rounded-lg border border-gray-300 bg-gray-50 p-4">
            <h3 class="mb-2 font-medium text-gray-700">
              Module {{ i + 1 }} : {{ module.learningModuleTitle }}
            </h3>
            <p class="text-sm text-gray-600">
              Ce module n'est pas encore visible pour les étudiants. Les résultats seront disponibles une fois le module publié.
            </p>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>
